// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getIntegration } from '@pages/template-library/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-integration-icon' )
export class AutomatorTemplateIntegrationIcon extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The code of the associated integration.
	 *
	 * @type {String}
	 * @property {String} integrationCode - Represents the code of the integration.
	 * Attribute name in HTML: `integration-code`.
	 */
	@property( { type: String, attribute: 'integration-code' } )
	accessor integrationCode;

	/**
	 * The size of the icon.
	 * Can accept predefined sizes such as `xsmall`, `small`, `large`, `xlarge`, `2x`, or specific pixel values like `16`, `24`, `32`, `40`, and `64`.
	 *
	 * @type {String}
	 * @property {String} size - Represents the size of the integration icon.
	 * Attribute name in HTML: `size`.
	 */
	@property( { type: String } )
	accessor size;

	/**
	 * Whether to show a tooltip with the name of the integration.
	 *
	 * @type {Boolean}
	 * @property {Boolean} showTooltip - Indicates if the tooltip should be displayed.
	 * Attribute name in HTML: `show-tooltip`.
	 * Default: `false`.
	 */
	@property( { type: Boolean, attribute: 'show-tooltip' } )
	accessor showTooltip = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		// If we don't have to show the tooltip, OR if the integration name is not available, just return the icon
		if ( ! this.showTooltip || _uo.utility.isEmpty( this.integration?.name ) ) {
			return this._templateLogIcon;
		}

		// Show the tooltip
		return html`
			<uo-tooltip>
				<span class="tooltip-nowrap">${ this.integration?.name }</span>

				<div slot="target">
					${ this._templateLogIcon }
				</div>
			</uo-tooltip>
		`;
	}

	/**
	 * Returns the main template that contains the icon
	 *
	 * @return {TemplateResult} The icon
	 */
	get _templateLogIcon() {
		return html`
			<div class="log-icon">
				<uo-icon integration="${ this.integration?.code }" size="${ ifDefined( this.size ) }"></uo-icon>
			</div>
		`;
	}

	/**
	 * Tries to get the integration name
	 *
	 * @return {String|undefined} The integration name
	 */
	get integration() {
		return getIntegration( 'code', this.integrationCode );
	}
}